import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { format, formatDistanceToNow } from 'date-fns'
import { MousePointerClick, Play, ShoppingCart, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { artworkApi } from '@/features/artworks/api/artworkApi'
import { PicassoFrame } from '../components/PicassoFrame'
import { useShopperSearch, useShopperSearches } from '../hooks/usePicasso'
import type { ShopperSearch } from '../api/picassoApi'

const SURFACE: Record<string, string> = {
  storefront: 'Search overlay',
  'storefront-search-page': '/search page',
  'ams-admin': 'Staff (this console)',
  'ams-recommend-worker': 'Recommend worker',
  unrecorded: 'Unrecorded',
}
const when = (at: number) => new Date(at * 1000)
const ms = (v: number | null | undefined) => (v == null ? '—' : v < 1000 ? `${Math.round(v)} ms` : `${(v / 1000).toFixed(1)} s`)

function Tile({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <Card>
      <CardContent className="space-y-1 p-4">
        <div className="text-xs text-slate-500">{label}</div>
        <div className="text-2xl font-semibold tabular-nums">{value}</div>
        {hint && <div className="text-[11px] text-slate-500">{hint}</div>}
      </CardContent>
    </Card>
  )
}

function SearchRow({ s, selected, onSelect }: { s: ShopperSearch; selected: boolean; onSelect: () => void }) {
  return (
    <button onClick={onSelect} className={`grid w-full grid-cols-[1fr_auto] items-center gap-3 rounded px-2 py-1.5 text-left text-sm ${selected ? 'bg-slate-900 text-white' : 'hover:bg-slate-50'}`}>
      <span className="min-w-0">
        <span className="block truncate">{s.q}</span>
        <span className={`block text-[11px] ${selected ? 'text-slate-300' : 'text-slate-500'}`}>
          {format(when(s.at), 'HH:mm')} · {s.path ?? '—'}{s.seedMode ? ` · ${s.seedMode}` : ''} · {ms(s.latencyMs)}
        </span>
      </span>
      <span className="flex items-center gap-2 text-xs tabular-nums">
        {s.results === 0 ? <span className={selected ? 'text-rose-300' : 'text-rose-700'}>0 results</span> : <span>{s.results}</span>}
        {s.events.click > 0 && <span className="inline-flex items-center gap-0.5"><MousePointerClick className="h-3 w-3" />{s.events.click}</span>}
        {s.events.addToCart > 0 && <span className="inline-flex items-center gap-0.5"><ShoppingCart className="h-3 w-3" />{s.events.addToCart}</span>}
      </span>
    </button>
  )
}

/** One served search: what was shown, what the shopper did with it, and a way to replay it. */
function Detail({ searchId, onClose }: { searchId: string; onClose: () => void }) {
  const { data, isLoading } = useShopperSearch(searchId)
  if (isLoading || !data) return <Skeleton className="h-96 w-full" />
  const clicked = new Map<number, string[]>()
  for (const e of data.events) {
    if (e.position == null) continue
    clicked.set(e.position, [...(clicked.get(e.position) ?? []), e.type])
  }
  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-lg font-medium">{data.q}</div>
          <div className="text-xs text-slate-500">
            {format(when(data.at), 'MMM d, HH:mm')} · {SURFACE[data.client ?? 'unrecorded'] ?? data.client}
            {data.refererPath && <> · from {data.refererPath}</>} · {ms(data.latencyMs)} · {data.backend ?? 'default'} / {data.path}
          </div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" asChild>
            <Link to={`/app/picasso?q=${encodeURIComponent(data.q)}`}><Play className="mr-1.5 h-3.5 w-3.5" /> Replay in console</Link>
          </Button>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="h-4 w-4" /></button>
        </div>
      </div>
      {data.artworks.length ? (
        <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 xl:grid-cols-6">
          {data.artworks.map((a) => {
            const did = clicked.get(a.position) ?? []
            return (
              <div key={a.position} className={`rounded p-1 ${did.length ? 'ring-2 ring-teal-600' : ''}`}>
                {a.missing ? (
                  <div className="flex aspect-square items-center justify-center rounded border bg-slate-50 text-[10px] text-slate-400">#{a.position} · {a.id}</div>
                ) : (
                  <Link to={`/app/artworks/${a.id}`}>
                    <img src={artworkApi.getArtworkImageUrl(a.id)} alt={a.name} loading="lazy" className="aspect-square w-full rounded border object-cover" />
                  </Link>
                )}
                <div className="mt-0.5 truncate text-[11px]">#{a.position} {a.name ?? ''}</div>
                {did.length > 0 && <div className="text-[10px] font-medium text-teal-700">{[...new Set(did)].join(', ')}</div>}
              </div>
            )
          })}
        </div>
      ) : (
        <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">This search served nothing.</p>
      )}
      <div className="grid gap-3 text-xs sm:grid-cols-2">
        <div>
          <div className="mb-1 font-medium text-slate-500">Plan</div>
          <pre className="max-h-56 overflow-auto rounded bg-slate-50 p-2 text-[11px]">{JSON.stringify(data.plan ?? null, null, 2)}</pre>
        </div>
        <div>
          <div className="mb-1 font-medium text-slate-500">What the shopper did</div>
          {data.events.length ? (
            <ul className="space-y-0.5">
              {data.events.map((e, i) => (
                <li key={i}>{format(when(e.at), 'HH:mm:ss')} · {e.type} · #{e.position ?? '—'} (artwork {e.artworkId ?? '—'})</li>
              ))}
            </ul>
          ) : <p className="text-slate-500">No clicks, carts or purchases recorded.</p>}
          {data.addons.length > 0 && <p className="mt-2 text-slate-500">Graph add-ons: {data.addons.join(', ')}</p>}
        </div>
      </div>
    </div>
  )
}

export function ShopperSearchesPage() {
  const [params, setParams] = useSearchParams()
  const client = params.get('client') ?? 'shoppers'
  const selected = params.get('s') ?? undefined
  const [limit, setLimit] = useState(200)
  const { data, isLoading, isFetching, error } = useShopperSearches({ limit, client })
  const set = (k: string, v?: string) => {
    const p = new URLSearchParams(params)
    if (v) p.set(k, v)
    else p.delete(k)
    setParams(p)
  }
  const sessions = data?.sessions ?? []
  const t = data?.totals

  return (
    <PicassoFrame
      title="Shopper searches"
      description="What shoppers searched on the storefront, grouped into their sessions, from the concierge's own search log: what each search served, how long it took, and what was clicked or carted. Sessions are hashed keys; nothing identifies a shopper."
    >
      {error ? (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4 text-sm text-amber-900">
            The concierge&apos;s search log could not be read. It lives in the Firestore project the concierge writes telemetry to
            (its <code>SESSIONS_FIRESTORE_PROJECT</code>); set <code>PICASSO_FIRESTORE_PROJECT</code> on the API if that is not AMS&apos;s own.
          </CardContent>
        </Card>
      ) : null}

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <Tile label="Searches" value={t ? t.searches.toLocaleString() : '—'} hint={`the latest ${limit}`} />
        <Tile label="Sessions" value={t ? t.sessions.toLocaleString() : '—'} />
        <Tile label="Served nothing" value={t ? `${t.zeroResults}` : '—'} hint={t && t.searches ? `${Math.round((100 * t.zeroResults) / t.searches)}% of searches` : undefined} />
        <Tile label="Led to a click" value={t ? `${t.withClick}` : '—'} hint={t && t.searches ? `${Math.round((100 * t.withClick) / t.searches)}% of searches` : undefined} />
        <Tile label="Median latency" value={ms(t?.medianLatencyMs)} />
      </div>

      <div className="flex flex-wrap items-center gap-2 text-sm">
        {/* "All shoppers" leaves out our own services (staff, workers); their own chips show them. */}
        {[{ name: 'shoppers', n: null as number | null }, ...(data?.clients ?? [])].map((c) => (
          <button
            key={c.name}
            onClick={() => set('client', c.name === 'shoppers' ? undefined : c.name)}
            className={`rounded-full border px-3 py-1 ${client === c.name ? 'bg-slate-900 text-white' : 'bg-white hover:bg-slate-50'}`}
          >
            {c.name === 'shoppers' ? 'All shoppers' : SURFACE[c.name] ?? c.name}
            {c.n != null && <span className="ml-1.5 tabular-nums opacity-60">{c.n}</span>}
          </button>
        ))}
        <select className="ml-auto rounded border bg-white px-2 py-1 text-xs" value={limit} onChange={(e) => setLimit(Number(e.target.value))}>
          {[100, 200, 500].map((n) => <option key={n} value={n}>Latest {n}</option>)}
        </select>
      </div>

      <div className="grid gap-6 xl:grid-cols-[26rem_minmax(0,1fr)]">
        <Card className={isFetching ? 'opacity-80' : undefined}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Sessions</CardTitle>
            <CardDescription>Newest first. A refinement within a session is the shopper telling search what it got wrong.</CardDescription>
          </CardHeader>
          <CardContent className="max-h-[720px] space-y-3 overflow-auto">
            {isLoading && <Skeleton className="h-64 w-full" />}
            {!isLoading && !sessions.length && <p className="py-6 text-center text-sm text-slate-500">No searches here.</p>}
            {sessions.map((g) => (
              <div key={g.session ?? g.searches[0].searchId} className="rounded-md border p-2">
                <div className="flex items-center justify-between px-2 pb-1 text-[11px] text-slate-500">
                  <span>{formatDistanceToNow(when(g.lastAt), { addSuffix: true })} · {g.searches.length} search{g.searches.length === 1 ? '' : 'es'}</span>
                  <span className="tabular-nums">
                    {g.clicks > 0 && <>{g.clicks} click{g.clicks === 1 ? '' : 's'}</>}
                    {g.carts > 0 && <> · {g.carts} carted</>}
                    {g.zeroResults > 0 && <span className="text-rose-700"> · {g.zeroResults} empty</span>}
                  </span>
                </div>
                {[...g.searches].reverse().map((s) => (
                  <SearchRow key={s.searchId} s={s} selected={selected === s.searchId} onSelect={() => set('s', s.searchId)} />
                ))}
              </div>
            ))}
          </CardContent>
        </Card>
        <Card className="min-w-0">
          <CardContent className="p-5">
            {selected ? <Detail searchId={selected} onClose={() => set('s')} /> : (
              <p className="py-16 text-center text-sm text-slate-500">Pick a search to see what it served and what the shopper did.</p>
            )}
          </CardContent>
        </Card>
      </div>
    </PicassoFrame>
  )
}
